import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SourceOfTruthBadge } from "@/components/ledger/SourceOfTruthBadge";
import { usePeople } from "@/lib/people-store";
import { isKarigarParty } from "@/lib/party-types";
import { useWorkerGoldBook, type WorkerGoldBookEntry } from "@/lib/worker-gold-book-store";
import { formatWeight, mgToGrams } from "@/lib/gold";
import { paiseToRupees } from "@/lib/billing-store";
import { toast } from "sonner";
import {
  Scale,
  Search,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  TrendingDown,
  Layers,
  Sparkles,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
  RotateCcw,
} from "lucide-react";

export const Route = createFileRoute("/workshop/reconciliation")({
  head: () => ({ meta: [{ title: "Wastage & Over-Loss Reconciliation · AVS ERP" }] }),
  component: ReconciliationPage,
});

export function ReconciliationPage() {
  const people = usePeople((s) => s.people);
  const karigars = useMemo(
    () => people.filter((p) => isKarigarParty(p) && p.active),
    [people],
  );

  const { entries, refresh } = useWorkerGoldBook();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const [selectedKarigarId, setSelectedKarigarId] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<"all" | "open" | "reconciled" | "overloss">("all");
  const [query, setQuery] = useState("");

  // Group entries by Nave issues
  const naveIssues = useMemo(() => {
    return entries.filter((e) => e.type === "given");
  }, [entries]);

  // Build the complete audit chain for each Nave issue
  const reconciliationRows = useMemo(() => {
    return naveIssues.map((nave) => {
      const returns = entries.filter((r) => r.type === "return" && r.naveEntryId === nave.id);
      const totalReturnedGrossMg = returns.reduce((sum, r) => sum + r.grossMg, 0);
      const totalReturnedFine995Mg = returns.reduce(
        (sum, r) => sum + (r.fine995Mg ?? r.fineMg),
        0,
      );
      const totalFilingsMg = returns.reduce(
        (sum, r) => sum + (r.recoveredFilingsMg ?? 0),
        0,
      );
      const totalLabourPaise = returns.reduce(
        (sum, r) => sum + (r.labourCashPaise ?? 0),
        0,
      );

      const issuedGrossMg = nave.grossMg;
      const issuedFine995Mg = nave.fine995Mg || nave.fineMg;

      const remainingGrossMg = Math.max(0, issuedGrossMg - totalReturnedGrossMg);
      const remainingFine995Mg = Math.max(0, issuedFine995Mg - totalReturnedFine995Mg);

      // Wastage calculations
      const actualLossMg = Math.max(0, issuedGrossMg - (totalReturnedGrossMg + totalFilingsMg));
      const allowedPct = nave.wastagePct ?? 1.5;
      const allowedLossMg = (totalReturnedGrossMg * allowedPct) / 100;
      const varianceMg = actualLossMg - allowedLossMg;
      const isOverLoss = varianceMg > 2; // tolerance > 2mg

      // Status determination
      const isFullyReturned = totalReturnedGrossMg >= issuedGrossMg && issuedGrossMg > 0;
      let status: "open" | "reconciled" | "overloss" = "open";
      if (isOverLoss) {
        status = "overloss";
      } else if (isFullyReturned) {
        status = "reconciled";
      }

      return {
        nave,
        returns,
        issuedGrossMg,
        issuedFine995Mg,
        totalReturnedGrossMg,
        totalReturnedFine995Mg,
        totalFilingsMg,
        totalLabourPaise,
        remainingGrossMg,
        remainingFine995Mg,
        actualLossMg,
        allowedLossMg,
        varianceMg,
        isOverLoss,
        isFullyReturned,
        status,
      };
    });
  }, [naveIssues, entries]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    return reconciliationRows.filter((row) => {
      if (selectedKarigarId !== "all" && row.nave.workerId !== selectedKarigarId) {
        return false;
      }
      if (filterStatus !== "all" && row.status !== filterStatus) {
        return false;
      }
      if (query.trim()) {
        const q = query.toLowerCase();
        const matchEntry = row.nave.entryNo.toLowerCase().includes(q);
        const matchWorker = row.nave.workerName.toLowerCase().includes(q);
        const matchItem = row.nave.particulars.toLowerCase().includes(q);
        if (!matchEntry && !matchWorker && !matchItem) return false;
      }
      return true;
    });
  }, [reconciliationRows, selectedKarigarId, filterStatus, query]);

  // Aggregated Totals
  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.issuedGrossMg += r.issuedGrossMg;
        acc.issuedFine995Mg += r.issuedFine995Mg;
        acc.returnedGrossMg += r.totalReturnedGrossMg;
        acc.returnedFine995Mg += r.totalReturnedFine995Mg;
        acc.remainingGrossMg += r.remainingGrossMg;
        acc.remainingFine995Mg += r.remainingFine995Mg;
        acc.overLossCount += r.isOverLoss ? 1 : 0;
        acc.totalLabourPaise += r.totalLabourPaise;
        return acc;
      },
      {
        issuedGrossMg: 0,
        issuedFine995Mg: 0,
        returnedGrossMg: 0,
        returnedFine995Mg: 0,
        remainingGrossMg: 0,
        remainingFine995Mg: 0,
        overLossCount: 0,
        totalLabourPaise: 0,
      },
    );
  }, [filteredRows]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Wastage & Over-Loss Reconciliation"
        subtitle="End-to-end SRM traceability: What was issued? → To whom? → What came back? → What was recovered? → What was lost? → What is payable? → Where is the remaining metal?"
        actions={<SourceOfTruthBadge variant="ledger" />}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl border border-border bg-card shadow-xs">
          <div className="text-xs text-muted-foreground">Total Metal Issued (Nave)</div>
          <div className="text-lg font-bold font-mono text-foreground mt-1">
            {formatWeight(totals.issuedGrossMg)}
          </div>
          <div className="text-[11px] font-mono text-amber-500 mt-0.5">
            {formatWeight(totals.issuedFine995Mg)} @995
          </div>
        </div>

        <div className="p-4 rounded-xl border border-border bg-card shadow-xs">
          <div className="text-xs text-muted-foreground">Total Received (Jama)</div>
          <div className="text-lg font-bold font-mono text-emerald-500 mt-1">
            {formatWeight(totals.returnedGrossMg)}
          </div>
          <div className="text-[11px] font-mono text-amber-500 mt-0.5">
            {formatWeight(totals.returnedFine995Mg)} @995
          </div>
        </div>

        <div className="p-4 rounded-xl border border-border bg-card shadow-xs">
          <div className="text-xs text-muted-foreground">Remaining in Custody</div>
          <div className="text-lg font-bold font-mono text-foreground mt-1">
            {formatWeight(totals.remainingGrossMg)}
          </div>
          <div className="text-[11px] font-mono text-amber-500 mt-0.5">
            {formatWeight(totals.remainingFine995Mg)} @995
          </div>
        </div>

        <div className="p-4 rounded-xl border border-border bg-card shadow-xs">
          <div className="text-xs text-muted-foreground">Over-Loss Exceptions</div>
          <div
            className={`text-lg font-bold font-mono mt-1 ${
              totals.overLossCount > 0 ? "text-rose-500" : "text-foreground"
            }`}
          >
            {totals.overLossCount}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Majuri: {paiseToRupees(totals.totalLabourPaise)}
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="p-4 rounded-xl border border-border bg-card flex flex-wrap gap-4 items-center justify-between shadow-xs">
        <div className="flex flex-wrap gap-3 items-center flex-1">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by GIV no, artisan, or particulars..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={selectedKarigarId}
            onChange={(e) => setSelectedKarigarId(e.target.value)}
          >
            <option value="all">All Karigars ({karigars.length})</option>
            {karigars.map((k) => (
              <option key={k.id} value={k.id}>
                {k.fullName}
              </option>
            ))}
          </select>

          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
          >
            <option value="all">All Statuses</option>
            <option value="open">Open / Pending Return</option>
            <option value="reconciled">Fully Reconciled</option>
            <option value="overloss">Over-Loss Exceptions</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/workshop/jama">
              <ArrowDownLeft className="h-4 w-4 mr-1 text-emerald-500" />
              Record Jama
            </Link>
          </Button>
          <Button asChild variant="default" size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
            <Link to="/settlement/new">
              <Wallet className="h-4 w-4 mr-1" />
              Settle Karigar
            </Link>
          </Button>
        </div>
      </div>

      {/* Reconciliation Lineage Cards / Table */}
      <div className="space-y-4">
        {filteredRows.length === 0 ? (
          <div className="p-12 text-center rounded-xl border border-border bg-card text-muted-foreground">
            No matching Nave issues or reconciliation chains found.
          </div>
        ) : (
          filteredRows.map((row) => (
            <div
              key={row.nave.id}
              className={`p-5 rounded-xl border bg-card space-y-4 shadow-xs transition-all ${
                row.isOverLoss
                  ? "border-rose-500/40 bg-rose-500/5"
                  : "border-border hover:border-primary/40"
              }`}
            >
              {/* Header: Issue Info & Status */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-bold text-sm text-foreground">
                    {row.nave.entryNo}
                  </span>
                  <span className="text-xs text-muted-foreground">{row.nave.date}</span>
                  <Badge variant="outline" className="font-normal text-xs">
                    {row.nave.workerName}
                  </Badge>
                  {row.nave.particulars && (
                    <span className="text-xs font-medium text-foreground">
                      · {row.nave.particulars}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {row.status === "reconciled" && (
                    <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                      Fully Reconciled
                    </Badge>
                  )}
                  {row.status === "open" && (
                    <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                      Custody Active
                    </Badge>
                  )}
                  {row.status === "overloss" && (
                    <Badge className="bg-rose-500/10 text-rose-500 border-rose-500/20 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Over-Loss Flagged
                    </Badge>
                  )}
                </div>
              </div>

              {/* 3-Column Lifecycle Comparison Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {/* 1. What was issued? (Nave) */}
                <div className="p-3 rounded-lg border border-border/60 bg-muted/20 space-y-1.5">
                  <div className="font-semibold text-foreground flex items-center gap-1.5">
                    <ArrowUpRight className="h-3.5 w-3.5 text-blue-500" />
                    1. Nave Issue (Outward)
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Issued Gross Wt:</span>
                    <span className="font-mono font-medium">{formatWeight(row.issuedGrossMg)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Touch Purity:</span>
                    <span className="font-mono font-medium">{row.nave.purity}‰</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fine Gold @995:</span>
                    <span className="font-mono font-semibold text-amber-500">
                      {formatWeight(row.issuedFine995Mg)}
                    </span>
                  </div>
                </div>

                {/* 2. What came back? (Jama) */}
                <div className="p-3 rounded-lg border border-border/60 bg-muted/20 space-y-1.5">
                  <div className="font-semibold text-foreground flex items-center gap-1.5">
                    <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-500" />
                    2. Jama Return (Inward)
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Returned Goods:</span>
                    <span className="font-mono font-medium">
                      {formatWeight(row.totalReturnedGrossMg)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fine Gold @995:</span>
                    <span className="font-mono font-semibold text-amber-500">
                      {formatWeight(row.totalReturnedFine995Mg)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Filings / Recovery:</span>
                    <span className="font-mono font-medium">
                      {formatWeight(row.totalFilingsMg)}
                    </span>
                  </div>
                </div>

                {/* 3. Loss & Balance Analysis */}
                <div className="p-3 rounded-lg border border-border/60 bg-muted/20 space-y-1.5">
                  <div className="font-semibold text-foreground flex items-center gap-1.5">
                    <Scale className="h-3.5 w-3.5 text-amber-500" />
                    3. Loss & Metal Position
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Actual Loss:</span>
                    <span className="font-mono font-medium">{formatWeight(row.actualLossMg)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Allowed Wastage Limit:</span>
                    <span className="font-mono font-medium">{formatWeight(row.allowedLossMg)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Variance:</span>
                    <span
                      className={`font-mono font-semibold ${
                        row.isOverLoss ? "text-rose-500" : "text-emerald-500"
                      }`}
                    >
                      {row.varianceMg > 0
                        ? `+${formatWeight(row.varianceMg)} (Over-loss)`
                        : `${formatWeight(row.varianceMg)} (OK)`}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-border/60 pt-1">
                    <span className="text-muted-foreground">Unreturned Custody:</span>
                    <span className="font-mono font-bold text-foreground">
                      {formatWeight(row.remainingGrossMg)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <div className="text-xs text-muted-foreground">
                  Accrued Majuri (Labour):{" "}
                  <span className="font-mono font-semibold text-foreground">
                    {paiseToRupees(row.totalLabourPaise)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {!row.isFullyReturned && (
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to="/workshop/jama"
                        search={{ karigarId: row.nave.workerId, naveId: row.nave.id }}
                      >
                        <ArrowDownLeft className="h-3.5 w-3.5 mr-1 text-emerald-500" />
                        Receive Jama
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm">
                    <Link to="/settlement/new" search={{ workerId: row.nave.workerId }}>
                      <Wallet className="h-3.5 w-3.5 mr-1" />
                      Karigar Settlement
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
